import { useState, useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { Eye, EyeOff, LogIn, ArrowLeft, Send } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase, getRedirectUrl } from "@/integrations/supabase/client";

const loginSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

const forgotPasswordSchema = z.object({
  email: z.string().email("Invalid email address"),
});

type LoginFormValues = z.infer<typeof loginSchema>;
type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>;

const LoginPage = () => {
  const {
    signInWithEmail,
    refreshSession,
    isLoading,
    user,
    session,
    authError,
    clearAuthError,
  } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  // Get the redirect path from the location state, or default to '/admin/dashboard'
  const from = location.state?.from?.pathname || "/admin/dashboard";

  // Redirect if user is already logged in
  useEffect(() => {
    if (user && !isLoading) {
      console.log("User already logged in, redirecting to dashboard", user);
      navigate(from, { replace: true });
    }
  }, [user, isLoading, navigate, from]);

  // Handle auth errors from the context
  useEffect(() => {
    if (authError) {
      setLoginError(authError);
      toast({
        title: "Access Denied",
        description: authError,
        variant: "destructive",
        duration: 8000, // Show longer for auth errors
      });
    }
  }, [authError, toast]);

  const loginForm = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const forgotPasswordForm = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: "",
    },
  });

  const onSubmit = async (values: LoginFormValues) => {
    let timeoutId: NodeJS.Timeout | null = null;
    
    try {
      setLoginError(null);
      clearAuthError(); // Clear any previous auth errors
      setIsProcessing(true);
      console.log("Login form submitted with:", values.email);

      // Set a timeout to show a processing message if it's taking too long
      timeoutId = setTimeout(() => {
        toast({
          title: "Still Processing",
          description:
            "We're retrieving your information. This might take a moment...",
          duration: 5000,
        });
      }, 3000);

      await signInWithEmail(values.email, values.password);
      console.log("signInWithEmail completed successfully");

      // Clear the timeout
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }

      // Reset retry count on successful login
      setRetryCount(0);

      // Show success message
      toast({
        title: "Login Successful",
        description: "Welcome to the Admin Portal!",
        variant: "default",
      });

      // Add a small delay to ensure the session is set
      setTimeout(() => {
        console.log("Navigating to dashboard after login", from);
        navigate(from, { replace: true });
      }, 500);
    } catch (error) {
      console.error("Login error:", error);

      // Clear the timeout if it exists
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }

      // Handle different types of errors
      let errorMessage =
        "Failed to log in. Please check your credentials and try again.";

      if (error instanceof Error) {
        // If it's a standard Error object, use its message
        errorMessage = error.message;

        // For Supabase auth errors, the message might include details
        if (error.message.includes("Invalid login credentials")) {
          errorMessage = "Invalid email or password. Please try again.";
        } else if (error.message.includes("Email not confirmed")) {
          errorMessage =
            "Your email has not been confirmed. Please check your inbox.";
        } else if (
          error.message.includes("timeout") ||
          error.message.includes("network")
        ) {
          errorMessage =
            "Network issue. Please check your connection and try again.";
        } else if (error.message.includes("Invalid Refresh Token")) {
          errorMessage = "Session expired. Trying to reconnect...";

          // If we have a refresh token error and haven't tried too many times
          if (retryCount < 2) {
            setRetryCount((prev) => prev + 1);

            // Try to refresh the session and retry login
            try {
              await refreshSession();
              // After refreshing session, retry the login
              setTimeout(() => {
                onSubmit(values);
              }, 1000);
              return; // Exit early to avoid showing error
            } catch (refreshError) {
              console.error("Failed to refresh session:", refreshError);
              errorMessage =
                "Could not restore your session. Please login again.";
            }
          } else {
            // If we've tried too many times, clear any stored auth data
            localStorage.removeItem("hriatnaeng-auth");
            errorMessage = "Please login again with your credentials.";
          }
        }
      }

      setLoginError(errorMessage);

      toast({
        title: "Login Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleForgotPassword = async (values: ForgotPasswordFormValues) => {
    try {
      setLoginError(null);
      clearAuthError(); // Clear any previous auth errors
      setIsProcessing(true);

      // Use the helper to generate a consistent redirect URL
      const redirectUrl = getRedirectUrl("/admin/reset-password");

      console.log("Setting password reset with redirect URL:", redirectUrl);

      const { error } = await supabase.auth.resetPasswordForEmail(
        values.email,
        {
          redirectTo: redirectUrl,
        }
      );

      if (error) {
        throw error;
      }

      // Show success state
      setForgotPasswordSent(true);

      toast({
        title: "Password Reset Email Sent",
        description: "Please check your email for the password reset link.",
        duration: 5000,
      });
    } catch (error) {
      console.error("Forgot password error:", error);

      let errorMessage = "Failed to send password reset email.";

      if (error instanceof Error) {
        errorMessage = error.message;
      }

      setLoginError(errorMessage);

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const renderLoginForm = () => (
    <Form {...loginForm}>
      <form onSubmit={loginForm.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={loginForm.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input
                  placeholder="email@example.com"
                  type="email"
                  autoComplete="email"
                  disabled={isLoading || isProcessing}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={loginForm.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Password</FormLabel>
              <FormControl>
                <div className="relative">
                  <Input
                    placeholder="••••••••"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    disabled={isLoading || isProcessing}
                    {...field}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 -translate-y-1/2"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isLoading || isProcessing}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                    <span className="sr-only">
                      {showPassword ? "Hide password" : "Show password"}
                    </span>
                  </Button>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex justify-end">
          <Button
            type="button"
            variant="link"
            className="p-0 h-auto text-sm text-muted-foreground"
            onClick={() => setShowForgotPassword(true)}
            disabled={isLoading || isProcessing}
          >
            Forgot password?
          </Button>
        </div>
        <Button
          type="submit"
          className="w-full"
          disabled={isLoading || isProcessing}
        >
          {isLoading || isProcessing ? (
            <div className="flex items-center gap-2">
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              <span>{isProcessing ? "Processing..." : "Logging in..."}</span>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <LogIn className="h-4 w-4" />
              <span>Login</span>
            </div>
          )}
        </Button>
      </form>
    </Form>
  );

  const renderForgotPasswordForm = () => (
    <>
      {forgotPasswordSent ? (
        <div className="text-center space-y-4">
          <div className="inline-flex p-3 mx-auto rounded-full bg-green-100">
            <Send className="h-6 w-6 text-green-600" />
          </div>
          <h3 className="text-lg font-medium">Password Reset Email Sent</h3>
          <p className="text-sm text-muted-foreground">
            We've sent a password reset link to your email address. Please check
            your inbox and follow the instructions.
          </p>
          <Button
            type="button"
            variant="outline"
            className="mt-2"
            onClick={() => {
              setShowForgotPassword(false);
              setForgotPasswordSent(false);
            }}
          >
            Return to Login
          </Button>
        </div>
      ) : (
        <Form {...forgotPasswordForm}>
          <form
            onSubmit={forgotPasswordForm.handleSubmit(handleForgotPassword)}
            className="space-y-4"
          >
            <div className="mb-4">
              <Button
                type="button"
                variant="ghost"
                className="p-0 h-auto text-sm flex items-center gap-1 text-muted-foreground"
                onClick={() => setShowForgotPassword(false)}
                disabled={isProcessing}
              >
                <ArrowLeft className="h-3 w-3" /> Back to login
              </Button>
            </div>

            <FormField
              control={forgotPasswordForm.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter your email address"
                      type="email"
                      disabled={isProcessing}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <p className="text-xs text-muted-foreground">
              We'll send you a link to reset your password. If you don't receive
              an email within a few minutes, please check your spam folder.
            </p>

            <Button type="submit" className="w-full" disabled={isProcessing}>
              {isProcessing ? (
                <div className="flex items-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                  <span>Sending...</span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Send className="h-4 w-4" />
                  <span>Send Reset Link</span>
                </div>
              )}
            </Button>
          </form>
        </Form>
      )}
    </>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center">
            {showForgotPassword ? "Reset Your Password" : "Admin Portal Login"}
          </CardTitle>
          <CardDescription className="text-center">
            {showForgotPassword
              ? "Enter your email to receive a password reset link"
              : "Enter your credentials to access the admin dashboard"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {(loginError || authError) && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{loginError || authError}</AlertDescription>
            </Alert>
          )}

          {showForgotPassword ? renderForgotPasswordForm() : renderLoginForm()}
        </CardContent>
        <CardFooter className="flex justify-center">
          <p className="text-center text-sm text-muted-foreground">
            Hriatna Eng Admin Portal
          </p>
        </CardFooter>
      </Card>
    </div>
  );
};

export default LoginPage;
